// import { TypedDocumentNode } from '@apollo/client';
import { ActionMap, TypedFunction } from '@trarc/pc-shared';
import { Context, Dispatch, ReactNode, Reducer } from 'react';

import { RegisteredRisk, RegisteredRiskFilters } from '../../types/api.graphql';

/**
 * The object type that is maintained by the RiskRegistry context and can be
 * consumed by any child of the RiskRegistry provider.
 */
export interface RiskRegistryContextState {
  filters: RegisteredRiskFilters;
  hasAnimatedIn: boolean;
  isExpandedViewModalOpen: boolean;
  isItemViewOpen: boolean;
  loading: boolean;
  selectedOrganizationIds: number[];
  registeredRisks: RegisteredRisk[];
  showNewRiskForm: boolean;
  selectedRiskRecord: RegisteredRisk | null;
}

/**
 * The RiskRegistry state context type that is returned after the creation of the state context.
 */
export type RiskRegistryDetailsContextType = Context<RiskRegistryContextState>;

// #START Hooks
/**
 * The function type for the `useRiskRegistryDetailsContext` hook. Takes no arguments and returns the current RiskRegistry context state.
 */
export type RiskRegistryStateHook = TypedFunction<[void], RiskRegistryContextState>;
// #END Hooks

//----------------
// #START Dispatch
//----------------
/**
 * The RiskRegistry dispatch types used by our {@link ActionMap} type to distinguish what payload the
 * dispatch action will require.
 */
export enum RiskRegistryDispatchTypes {
  /**
   * Sets the risk registry data filters
   */
  setFilters = 'Set Filters',
  /**
   * Resets the risk registry data filters
   */
  resetFilters = 'Reset Filters',
  /**
   * Resets the view to the action panel when triggered by causing the animation to occur
   * and transition to the target panel.
   */
  setPanelView = 'Set Panel View',
  /**
   * Resets the view to the action panel when triggered by causing the animation to occur
   * and transition back to the action panel.
   */
  resetPanelView = 'Reset Panel View',
  /**
   * Sets state's registered risks upon data fetch and on refetch
   */
  setRegisteredRisks = 'Set Registered Risk',
  /**
   * Sets the selected organization Ids
   */
  setSelectedOrganizationIds = 'Set Selected Organization Ids',
  /**
   * Sets the selected risk record
   */
  setSelectedRiskRecord = 'Set Selected Risk Record',
  /**
   * toggles the expanded view modal for registered risk record view
   */
  toggleExpandedViewModal = 'Toggle Expanded View Modal',
  /**
   * toggles show/hide the new risk form for the action panel
   */
  toggleShowNewRiskForm = 'Toggle Show New Risk Form',
}

/**
 * An object mapping each dispatch type to the payload required for that dispatch action.
 */
interface RiskRegistryDispatchPayloadMap {
  [RiskRegistryDispatchTypes.setFilters]: RegisteredRiskFilters;
  [RiskRegistryDispatchTypes.resetFilters]: never;
  [RiskRegistryDispatchTypes.setPanelView]: never;
  [RiskRegistryDispatchTypes.resetPanelView]: never;
  [RiskRegistryDispatchTypes.setRegisteredRisks]: RegisteredRisk[];
  [RiskRegistryDispatchTypes.setSelectedOrganizationIds]: number[];
  [RiskRegistryDispatchTypes.setSelectedRiskRecord]: RegisteredRisk | null;
  [RiskRegistryDispatchTypes.toggleExpandedViewModal]: never;
  [RiskRegistryDispatchTypes.toggleShowNewRiskForm]: never;
}

/**
 * The union type of dispatch actions that results from using {@link ActionMap}.
 */
export type RiskRegistryActions =
  ActionMap<RiskRegistryDispatchPayloadMap>[keyof ActionMap<RiskRegistryDispatchPayloadMap>];

/**
 * The type for the dispatch provider as well as the dispatch function itself.
 */
export type RiskRegistryDispatchState = Dispatch<RiskRegistryActions>;

/**
 * The context type for the RiskRegistry dispatch context. It can be undefined so that
 * we can verify anything attempting to use the dispatch is a child of the context.
 */
export type RiskRegistryDispatch = RiskRegistryDispatchState | undefined;

/**
 * The RiskRegistry dispatch context type that is returned after the creation of the dispatch context.
 */
export type RiskRegistryDispatchContextType = Context<RiskRegistryDispatch>;

/**
 * Note: use of an interface for a single returned function ensures the name of the function will be the same
 * across each use of the hook when destructured from the returned values of the hook.
 */
export interface RiskRegistryDispatchHookReturn {
  /**
   * The named function that is returned to make dispatch calls to the context.
   */
  riskRegistryDispatch: RiskRegistryDispatchState;
}

/**
 * The function type for the `useRiskRegistryDetailsContext` hook. Takes no arguments and returns a dispatch function for the RiskRegistry context.
 */
export type RiskRegistryDispatchHook = TypedFunction<[void], RiskRegistryDispatchHookReturn>;

// #END Dispatch

// #START Reducer
/**
 * The RiskRegistry reducer function type definition. Primarily used by any `useReducer`
 * that uses the function or the function itself.
 */
export type RiskRegistryReducerFunction = Reducer<RiskRegistryContextState, RiskRegistryActions>;
// #END Reducer

// #START Provider
/**
 * The object types for the props of the RiskRegistry provider component.
 */
export interface RiskRegistryProviderProps {
  /**
   * Any element that will render as a child of the RiskRegistry context.
   */
  children?: ReactNode;
}
